'use client'

import { useEffect, useState } from 'react'
import Link from '@/components/Link'
import { Analytics, type AnalyticsConfig } from 'pliny/analytics'
import { Analytics as VercelAnalytics } from '@vercel/analytics/react'
import AdSenseScript from '@/components/ads/AdSenseScript'
import OneSignalSetup from '@/app/OneSignalSetup'
import siteMetadata from '@/data/siteMetadata'

const STORAGE_KEY = 'allisports-cookie-consent'
type ConsentChoice = 'all' | 'essential'

export default function ConsentManager() {
  const [choice, setChoice] = useState<ConsentChoice | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ConsentChoice | null
    if (saved === 'all' || saved === 'essential') {
      setChoice(saved)
    } else {
      setIsOpen(true)
    }

    const openSettings = () => setIsOpen(true)
    window.addEventListener('allisports:manage-consent', openSettings)
    return () => window.removeEventListener('allisports:manage-consent', openSettings)
  }, [])

  function saveChoice(nextChoice: ConsentChoice) {
    localStorage.setItem(STORAGE_KEY, nextChoice)
    setChoice(nextChoice)
    setIsOpen(false)
    window.dispatchEvent(new Event('allisports:consent-change'))
  }

  return (
    <>
      {choice === 'all' && (
        <>
          <Analytics analyticsConfig={siteMetadata.analytics as AnalyticsConfig} />
          <VercelAnalytics />
          <OneSignalSetup />
          <AdSenseScript />
        </>
      )}

      {isOpen && (
        <aside
          className="fixed right-3 bottom-3 left-3 z-[100000] mx-auto max-w-3xl rounded-[20px] border border-white/15 bg-[#11141c]/98 p-4 text-white shadow-[0_24px_80px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:p-5"
          aria-label="Cookie preferences"
        >
          <div className="sm:grid sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6">
            <div>
              <h2 className="text-lg font-bold tracking-[-0.03em]">Your privacy choices</h2>
              <p className="mt-1 text-sm leading-5 text-white/60">
                Allow optional analytics, notifications and future ads, or continue with essential
                storage only.
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => saveChoice('all')}
                  className="h-10 rounded-full bg-[var(--alli-red)] px-4 text-xs font-bold transition hover:bg-[var(--alli-red-strong)]"
                >
                  Allow optional
                </button>
                <button
                  type="button"
                  onClick={() => saveChoice('essential')}
                  className="h-10 rounded-full border border-white/15 px-4 text-xs font-bold transition hover:border-white/40"
                >
                  Essential only
                </button>
              </div>
              <Link
                href="/cookies"
                className="mt-2 block text-center text-xs font-semibold text-white/50 underline decoration-white/25 underline-offset-4 hover:text-white"
              >
                Cookie details
              </Link>
            </div>
          </div>
        </aside>
      )}
    </>
  )
}
